/* eslint-disable camelcase */
// Contract for the Shopper AI Message Details endpoint.
// Locked to Chase Wackerfuss's "Autos Traceability Metadata Payload" handoff.

export const CUSTOMER_STATE = {
  PROSPECTIVE_CUSTOMER: 'prospective_customer',
  ONE_TIME_PURCHASER: 'one_time_purchaser',
  REPEAT_CUSTOMER: 'repeat_customer',
} as const;
export type CustomerState = typeof CUSTOMER_STATE[keyof typeof CUSTOMER_STATE];

export const LIFECYCLE_STAGE = {
  ACQUISITION: 'acquisition',
  INTEREST: 'interest',
  CONSIDERATION: 'consideration',
  CONVERSION: 'conversion',
  POST_PURCHASE: 'post_purchase',
  ADVOCACY_LOYALTY: 'advocacy_loyalty',
  REACTIVATION: 'reactivation',
} as const;
export type LifecycleStage =
  typeof LIFECYCLE_STAGE[keyof typeof LIFECYCLE_STAGE];

export const MESSAGE_TYPE = {
  ENGAGEMENT: 'engagement',
  BRAND_INSPIRATION: 'brand_inspiration',
  PRODUCT_EDUCATION: 'product_education',
  CONVERSION: 'conversion',
  TRANSACTIONAL: 'transactional',
} as const;
export type MessageType = typeof MESSAGE_TYPE[keyof typeof MESSAGE_TYPE];

export const GROUP = {
  ADVOCACY: 'advocacy',
  BRAND_STORY: 'brand_story',
  DISCOVERY_QUESTION: 'discovery_question',
  EDUCATION: 'education',
  LOYALTY: 'loyalty',
  NUDGE: 'nudge',
  OFFER: 'offer',
  POST_PURCHASE_CARE: 'post_purchase_care',
  SOCIAL_PROOF: 'social_proof',
  SUPPORT: 'support',
} as const;
export type Group = typeof GROUP[keyof typeof GROUP];

export const URGENCY = {
  NONE: 'none',
  LIGHT: 'light',
  HEAVY: 'heavy',
} as const;
export type Urgency = typeof URGENCY[keyof typeof URGENCY];

export const FOCUS = {
  PRODUCT: 'product',
  BRAND: 'brand',
  BLENDED: 'blended',
  GENERAL: 'general',
} as const;
export type Focus = typeof FOCUS[keyof typeof FOCUS];

// Snake-case keys mirror Chase's JSON contract verbatim — the FE accepts the
// payload as-is and only camelCases at component boundaries when needed.
export interface MessageDetails {
  display_name: string;
  description: string;
  parent_id: string | null;
  full_reference_text: string | null;
  customer_states: CustomerState[];
  lifecycle_stages: LifecycleStage[];
  message_type: MessageType | null;
  group: Group | null;
  urgency: Urgency | null;
  focus: Focus | null;
  // Open-ended free-form tags. Treat unknown values as displayable, not
  // invalid — Chase's contract explicitly leaves this set open.
  levers: string[];
  // Three-state booleans. `null` means "not classified" and must render
  // distinctly from `false`. See drawer section spec.
  is_conversational: boolean | null;
  references_previous_offer: boolean | null;
  offers_new_discount: boolean | null;
  price_increase: boolean | null;
  scarcity: boolean | null;
  timing_pressure: boolean | null;
  competition: boolean | null;
  // Resolved runtime values used for this message. Null/empty for older
  // messages or when the backend hasn't sent one yet — render rows only when
  // present.
  runtime_context: RuntimeContext | null;
  // Subscriber opt-in verification ("AI Verified"), resolved by the details
  // endpoint. Null when the message has no subscriber.
  verified: boolean | null;
}

// Runtime-context values mirror the API's all-Optional RuntimeContext*
// serializers: every field can be null, and stored snapshots can also carry
// empty strings (e.g. a product captured with a handle but no title). Render
// with per-field fallbacks; partial data must never invalidate the payload.
export interface RuntimeContextOffer {
  title: string | null;
  discount_value: string | null;
  discount_code: string | null;
}

export interface RuntimeContextProduct {
  id: string | null;
  title: string | null;
  handle: string | null;
  image_url: string | null;
}

export interface RuntimeContextContent {
  url: string | null;
  destination: string | null;
}

export interface RuntimeContextToneOfVoice {
  casual_formal: number | null;
  gentle_bold: number | null;
  product_lifestyle: number | null;
  concise_chatty: number | null;
  // Not served by the details endpoint yet; optional so existing payloads and
  // presets stay valid. Renders automatically once the API includes it.
  relaxed_urgent?: number | null;
}

export interface RuntimeContext {
  offer: RuntimeContextOffer | null;
  products: RuntimeContextProduct[];
  content: RuntimeContextContent[];
  tone_of_voice: RuntimeContextToneOfVoice | null;
}
